/**
 * Copyright (c) Streamlit Inc. (2018-2022) Snowflake Inc. (2022-2026)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  memo,
  type ReactElement,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"

import { Config, type Exception as ExceptionProto } from "@streamlit/protobuf"
import { isLocalhost } from "@streamlit/utils"

import { LibConfigContext } from "~lib/components/core/LibConfigContext"
import {
  SkillsInstallContext,
  useSkillsCalloutSlot,
} from "~lib/components/core/SkillsInstallContext"
import { StyledCode } from "~lib/components/elements/CodeBlock/styled-components"
import AlertContainer, {
  Kind,
} from "~lib/components/shared/AlertContainer/AlertContainer"
import { StyledStackTrace } from "~lib/components/shared/ErrorElement/styled-components"
import StreamlitMarkdown from "~lib/components/shared/StreamlitMarkdown/StreamlitMarkdown"
import { useCopyToClipboard } from "~lib/hooks/useCopyToClipboard"
import { notNullOrUndefined } from "~lib/util/utils"

import SkillsInstallCallout from "./SkillsInstallCallout"
import {
  StyledExceptionLinkButton,
  StyledExceptionLinks,
  StyledExceptionMessage,
  StyledExceptionWithCallout,
  StyledExceptionWrapper,
  StyledMessageType,
  StyledStackTraceContent,
  StyledStackTraceRow,
  StyledStackTraceTitle,
} from "./styled-components"

export interface ExceptionElementProps {
  element: ExceptionProto
}

interface ExceptionMessageProps {
  type: string
  message: string
  messageIsMarkdown: boolean
}

interface StackTraceProps {
  stackTrace: string[]
}

/**
 * Return true if the string is non-null and non-empty.
 */
function isNonEmptyString(value: string | null | undefined): boolean {
  return notNullOrUndefined(value) && value !== ""
}

function ExceptionMessage({
  type,
  message,
  messageIsMarkdown,
}: Readonly<ExceptionMessageProps>): ReactElement {
  // Build the message display.
  // On the backend, we use the StreamlitException type for errors that
  // originate from inside Streamlit. These errors have Markdown-formatted
  // messages, and so we wrap those messages inside our Markdown renderer.

  if (messageIsMarkdown) {
    let markdown = message ?? ""
    if (type.length !== 0) {
      markdown = `**${type}**: ${markdown}`
    }
    return <StreamlitMarkdown source={markdown} allowHTML={false} />
  }
  return (
    <>
      <StyledMessageType>{type}</StyledMessageType>
      {type.length !== 0 && ": "}
      {isNonEmptyString(message) ? message : null}
    </>
  )
}

function StackTrace({ stackTrace }: Readonly<StackTraceProps>): ReactElement {
  // Build the stack trace display, if we got a stack trace.
  return (
    <div>
      <StyledStackTraceTitle>Traceback:</StyledStackTraceTitle>
      <StyledStackTrace>
        <StyledStackTraceContent>
          <StyledCode wrapLines={false}>
            {stackTrace.map((row: string, index: number) => (
              <StyledStackTraceRow
                // TODO: Update to match React best practices
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                data-testid="stExceptionTraceRow"
              >
                {row}
              </StyledStackTraceRow>
            ))}
          </StyledCode>
        </StyledStackTraceContent>
      </StyledStackTrace>
    </div>
  )
}

/**
 * Functional element representing formatted text.
 */
function ExceptionElement({
  element,
}: Readonly<ExceptionElementProps>): ReactElement {
  const { showErrorLinks = Config.ShowErrorLinks.SHOW_ERROR_LINKS_AUTO } =
    useContext(LibConfigContext)

  const shouldShowLinks =
    showErrorLinks === Config.ShowErrorLinks.SHOW_ERROR_LINKS_TRUE ||
    (showErrorLinks === Config.ShowErrorLinks.SHOW_ERROR_LINKS_AUTO &&
      isLocalhost())

  // Offer a one-click "install Streamlit skills" CTA in local development, in its
  // own box directly below this error. Reuses `shouldShowLinks` — the same
  // localhost gate as the AI help links above — and scopes tightly so the nudge
  // only appears where the skills would actually help:
  //   - Streamlit-raised exceptions only (`element.isStreamlitException`) —
  //     API misuse the skills can fix, not arbitrary user errors like a
  //     ZeroDivisionError. The broad startup toast (#15473) still covers those.
  //   - Real errors, not warnings (`!element.isWarning`).
  // At most one callout shows app-wide, enforced by claiming a single shared
  // slot. The slot is sticky once claimed, so the callout isn't yanked when a
  // successful install flips the recommendation off — it dismisses itself after
  // confirming.
  const skillsInstall = useContext(SkillsInstallContext)
  const skillsCalloutEligible =
    shouldShowLinks &&
    !element.isWarning &&
    element.isStreamlitException &&
    skillsInstall.enabled
  // The slot is only claimed if this box is actually on screen — a collapsed
  // expander or an inactive tab keeps its errors mounted, and one of those must
  // not take the single slot from a visible error.
  const wrapperRef = useRef<HTMLDivElement>(null)
  const ownsSkillsCalloutSlot = useSkillsCalloutSlot(
    skillsCalloutEligible,
    wrapperRef
  )
  const [skillsCalloutDismissed, setSkillsCalloutDismissed] = useState(false)
  const handleSkillsCalloutDismiss = useCallback(
    () => setSkillsCalloutDismissed(true),
    []
  )
  const showSkillsCallout = ownsSkillsCalloutSlot && !skillsCalloutDismissed

  const formattedExceptionShort = `${element.type}: ${element.message}`
  const formattedExceptionFull = `${formattedExceptionShort}\n\n${element.stackTrace?.join(
    "\n"
  )}`

  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
    formattedExceptionShort
  )}`
  const chatGptUrl = `https://chatgpt.com/?q=${encodeURIComponent(
    formattedExceptionFull
  )}`

  const { copyToClipboard } = useCopyToClipboard()

  const handleCopy = useCallback(() => {
    copyToClipboard(formattedExceptionFull)
  }, [copyToClipboard, formattedExceptionFull])

  return (
    <StyledExceptionWithCallout ref={wrapperRef}>
      <div className="stException" data-testid="stException">
        <AlertContainer kind={element.isWarning ? Kind.WARNING : Kind.ERROR}>
          <StyledExceptionWrapper>
            <StyledExceptionMessage data-testid="stExceptionMessage">
              <ExceptionMessage
                type={element.type}
                message={element.message}
                messageIsMarkdown={element.messageIsMarkdown}
              />
            </StyledExceptionMessage>
            {element.stackTrace && element.stackTrace.length > 0 ? (
              <StackTrace stackTrace={element.stackTrace} />
            ) : null}
            {shouldShowLinks && (
              <StyledExceptionLinks>
                <StyledExceptionLinkButton onClick={handleCopy}>
                  Copy
                </StyledExceptionLinkButton>
                <a href={searchUrl} target="_blank" rel="noopener noreferrer">
                  Ask Google
                </a>
                <a href={chatGptUrl} target="_blank" rel="noopener noreferrer">
                  Ask ChatGPT
                </a>
              </StyledExceptionLinks>
            )}
          </StyledExceptionWrapper>
        </AlertContainer>
      </div>
      {/* Its own box below the error, not a row inside it (per the design), so
          `stException` keeps meaning "the error box" for anyone targeting it. */}
      {showSkillsCallout && (
        <SkillsInstallCallout
          enabled={skillsCalloutEligible}
          onInstall={skillsInstall.onInstall}
          onShown={skillsInstall.onShown}
          onDismiss={handleSkillsCalloutDismiss}
        />
      )}
    </StyledExceptionWithCallout>
  )
}

export default memo(ExceptionElement)
